/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2018, 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./SlotAPISpec',
	'../SlotAPI',
	'../../visualizations/interactions/BinningActionsUtils',
	'../../apiHelpers/SlotAPIHelper',
	'../../widgets/livewidget/nls/StringResources',
	'../../util/TransactionUtil'

], (_, APIFactory, SlotAPISpec, SlotAPI, BinningActionsUtils, SlotAPIHelper, StringResources, TransactionUtil) => {

	class SlotImpl extends SlotAPISpec{
		constructor(slotDefinitionAPI, filterSupport, slotsImpl, transaction, locale, visualizationImpl, dataModel, slotMappingModel) {
			super();
			// @todo remove localFilters, slotDfn (for now used in creating MappedDataItem) plus SlotAPI
			this.slotDefinitionAPI = slotDefinitionAPI;
			// @todo need to revise by using events
			this.filterSupport = filterSupport;
			this.metadataManager = filterSupport.getMetadata();
			this.transaction = transaction;
			this.locale = locale;
			this.slotsImpl = slotsImpl;
			this.visualizationImpl = visualizationImpl;

			this.dataModel = dataModel;
			this.slotMappingModel = slotMappingModel;

			this.api = APIFactory.createAPI(this, [SlotAPI]);
		}

		setDefinition(slotDefinitionAPI){
			this.slotDefinitionAPI = slotDefinitionAPI;
		}

		destroy() {

			this.slotDefinitionAPI = null;
			this.filterSupport = null;
			this.metadataManager = null;
			this.transaction = null;
			this.locale = null;
			this.slotsImpl = null;
			this.visualizationImpl = null;
			this.dataModel = null;
			this.slotMappingModel = null;
			this.api = null;
		}

		getAPI() {
			return this.api ;
		}

		getId() {
			return this.getDefinition().getId();
		}

		getLocalFilters() {
			return this.filterSupport.getLocalFilters();
		}

		getDataSource() {
			return this.slotsImpl.getDataSource();
		}

		resolveDataViewId() {
			let viewId;
			// step 1 : check the first item (if any) that belongs to this slot
			let dataItemList = this.getDataItemList();
			if(dataItemList.length > 0){
				viewId = this.dataModel.getDataViewIdForDataItem(dataItemList[0].getId());
			}

			// Step 2: If we have no mapped dataitem, search the slots that belong to the same dataset
			if (!viewId){
				let slot;
				const slotDefinition = this.getDefinition();
				const slotDefinitionList = this.visualizationImpl.getDefinition().getSlotList();
				for (let i = 0 ; i < slotDefinitionList.length ; i ++){
					if (slotDefinition.getId() !== slotDefinitionList[i].getId() &&  slotDefinition.getDatasetIdList()[0] === slotDefinitionList[i].getDatasetIdList()[0]){
						// the slots are part of the same dataset
						// see if it has mapped dataitems.. then use the same view
						slot = this.slotsImpl.getSlot(slotDefinitionList[i].getId());
						dataItemList = slot.getDataItemList();
						if(dataItemList.length > 0){
							viewId = this.dataModel.getDataViewIdForDataItem(dataItemList[0].getId());
							break;
						}
					}
				}
			}

			if (!viewId) {
				// we could not find a view.. we create a new one
				const dataView = this.dataModel.createDataView();
				const dataSource = this.visualizationImpl.getDataSource();
				// default the modelRef if we have other views (NOTE: content datasources/views have no modelRefs)
				// PA Support: In PA, the dataSource is bound to the content.  This means that the dataSourceId and content Id are identical
				// This situation does not currently occur in CA where we use the modelRef of the view to determine the dataSourceId.
				const contentId = this.visualizationImpl.content && this.visualizationImpl.content.getId();
				if (dataSource && dataSource.getId()!==contentId){
					this.dataModel.setModelRefInAllViews(dataSource.getId());
				}
				viewId = dataView.id;
			}

			return viewId;
		}


		/**
		 * Return the slotImpl associated with the multi-measure series. We use the implementation so that
		 * any operations are considered inernal and no events will be generated.
		 */
		getMultiMeasuresSeriesSlot() {
			let multiMeasureSlot;

			const dataset = this.getDefinition().getDatasetIdList()[0];
			const customMultiMeasureSlotId = this.slotMappingModel.getMultiMeasureSlotId(dataset);

			if(customMultiMeasureSlotId) {
				// slot may not exist, e.g changing visualization from one with slot series to one with color.
				multiMeasureSlot = this.slotsImpl.getSlotImpl(customMultiMeasureSlotId);
			}

			if(!multiMeasureSlot) {
				const defaultMultiMeasureSlot = SlotAPIHelper.getMultiMeasureSeriesSlot(
					this.visualizationImpl.getAPI(),
					dataset
				).getId();
				multiMeasureSlot = this.slotsImpl.getSlotImpl(defaultMultiMeasureSlot);
			}

			return multiMeasureSlot;
		}

		getMultiMeasureSeriesPosition() {
			const dataset = this.getDefinition().getDatasetIdList()[0];
			return this.slotMappingModel.getMultiMeasurePosition(dataset);
		}

		/**
		 * Check how many measure we have in the slot and decide if we need to add, remove or update the measure series slot
		 */
		_processMultiMeasure() {
			if(!this.getDefinition().getProperty('multiMeasure')) {
				return;
			}
			const targetSlot = this.getMultiMeasuresSeriesSlot();
			if (targetSlot) {
				const currentDataItemList = this.getDataItemList();
				const multiMeasuresLabel = StringResources.get('MeasuresGroupCaption', {
					count: currentDataItemList.length
				});

				const multiMeasureDataItemId = SlotAPIHelper.MULTI_MEASURES_SERIES + '_' + targetSlot.getDefinition().getDatasetIdList()[0];
				const multiMeasureRequired = (currentDataItemList.length) > 1;
				const multiMeasureDataItem = this.slotsImpl.getDataItemImpl(multiMeasureDataItemId);


				const createMultiMeasureDataItem = function() {
					// Use the implementation to keep the actions internal (i.e. no events)
					this.slotsImpl.createDataItems([{
						id: multiMeasureDataItemId,
						columnId: SlotAPIHelper.MULTI_MEASURES_SERIES,
						itemLabel: multiMeasuresLabel
					}]);
					targetSlot.addDataItemsMapping([multiMeasureDataItemId], this.getMultiMeasureSeriesPosition());
				}.bind(this);

				const deleteMultiMeasureDataItem = function() {
					// Use the implementation to keep the actions internal (i.e. no events)
					this.slotsImpl.deleteDataItems([multiMeasureDataItemId]);
					targetSlot.removeDataItemsMapping([multiMeasureDataItemId]);
				}.bind(this);

				const updateMultiMeasureDataItem = function() {
					// Use the implementation to keep the actions internal (i.e. no events)
					this.slotsImpl.getDataItemImpl(multiMeasureDataItem.getId()).setLabel(multiMeasuresLabel);
				}.bind(this);

				if (multiMeasureRequired && multiMeasureDataItem) {
					updateMultiMeasureDataItem();
				} else if(multiMeasureRequired && !multiMeasureDataItem) {
					createMultiMeasureDataItem();
				} else if(!multiMeasureRequired && multiMeasureDataItem) {
					deleteMultiMeasureDataItem();
				}
			}
		}

		getDataItemIndex (id) {
			const slotModel = this.slotMappingModel.getSlot(this.getId());
			return slotModel.dataItems.indexOf(id);
		}

		// Return the actual instance and not just the API interface
		getDataItemImpl(dataItemId){
			let impl;
			// Search the mapping model
			const slotModel = this.slotMappingModel.getMappedSlot(dataItemId);
			if (slotModel && slotModel.name === this.getId()){
				// the dataItem is mapped to this slot
				impl = this.slotsImpl.getDataItemImpl(dataItemId);
			}
			return impl;
		}

		getDataItem(dataItemId) {
			const impl = this.getDataItemImpl(dataItemId);
			return impl ? impl.getAPI(): null;
		}

		addDataItems(dataItemSpecList, position, transactionToken){
			const subTransaction = this.transaction.startTransaction(transactionToken);
			// Use the API to trigger the event
			const dataItemList = this.slotsImpl.getAPI().createDataItems(dataItemSpecList, subTransaction);
			this.getAPI().addDataItemsMapping(dataItemList.map(dataItem => dataItem.getId()), position, subTransaction);
			this.transaction.endTransaction(subTransaction);
			return dataItemList;
		}

		removeDataItems(dataItemIdList, transactionToken){
			const subTransaction = this.transaction.startTransaction(transactionToken);

			this._removeSelectionsOnUnusedDataItems(dataItemIdList, subTransaction);
			this._removedUnmappedOrdinalSlotsWithFilters(dataItemIdList.map(id => {
				const dataItem = this.slotsImpl.getDataItemImpl(id);
				if (dataItem){
					return {
						columnId: dataItem.getColumnId(),
						aggregationType: dataItem.getAggregation()
					};
				}
			}), subTransaction);

			this.getAPI().removeDataItemsMapping(dataItemIdList, subTransaction);
			// Use the API to trigger the event
			this.slotsImpl.getAPI().deleteDataItems(dataItemIdList, subTransaction);
			this.transaction.endTransaction(subTransaction);

		}

		_removeSelectionsOnUnusedDataItems(dataItemIdList, transactionToken) {
			const columnIdList = dataItemIdList.map(id => this.getDataItemImpl(id).getColumnId());
			this.filterSupport.clearSelectionsByDataItemIds(columnIdList, TransactionUtil.transactionTokenToOptions(transactionToken));
		}

		_removedUnmappedOrdinalSlotsWithFilters(unusedFilterRef, transactionToken) {
			const localFilters = this.getLocalFilters();
			const filterEntryToRemove = _.find(localFilters.models, (filterEntry) => {
				if (filterEntry.isMissing) {
					return _.findWhere(unusedFilterRef, _.pick(filterEntry, 'columnId'));
				} else if (filterEntry.aggregationType) {
					/* Remove obsolete ordinal filter when there is no match for both column id and aggregation type.
					1. when the data item is not projected anymore
						- eg. projected data  in the slot but no aggregation type matching
						- eg. projected data items with ids 'col1' (sum), 'col1' (count), col2' but the filter was set on 'col1' (avg) */
					return _.findWhere(unusedFilterRef, _.pick(filterEntry, 'columnId', 'aggregationType'));
				} else {
					return _.findWhere(unusedFilterRef, _.pick(filterEntry, 'columnId'));
				}
			});
			if (filterEntryToRemove) {
				localFilters.removeFilterEntry(filterEntryToRemove);
				localFilters.allFilterModificationComplete(TransactionUtil.transactionTokenToOptions(transactionToken));
			}
		}


		removeDataItemsMapping(dataItemIds, transactionToken) {
			const result = [];
			const subTransaction = this.transaction.startTransaction(transactionToken);
			const options = this._convertTransactionTokenToLegacyOptions(subTransaction);

			const localFilters = this.getLocalFilters();
			//for any changes in binning we need clear all selection filters, it will not be part of undo/redo
			_.each(dataItemIds, (dataItemId) => {
				const dataItem = this.getDataItemImpl(dataItemId);
				if (dataItem) {
					// TODO livewidget_cleanup -- -does this cleanup belong here ?
					if (dataItem.getBinning()) {
						BinningActionsUtils.removeCompoundFilters(this, localFilters, options);
						const binnedFilters = BinningActionsUtils.getBinningFilters(localFilters, dataItem);
						BinningActionsUtils.removeLocalBinnedFilterEntries(localFilters, binnedFilters, options);
					}
					this.slotMappingModel.unmapDataItemsFromSlots([dataItemId]);
					dataItem.setSlot(null);
					result.push(dataItem);
				}else {
					result.push(null);
				}
			});
			this._processMultiMeasure();
			this.transaction.endTransaction(subTransaction);
			return result;
		}


		addDataItemsMapping(dataItemIdList, position = -1, transactionToken) {
			dataItemIdList.forEach((id, index) => {
				const dataItem = this.slotsImpl.getDataItemImpl(id);

				// When dataItem is empty, we remove it from dataItemIdList, here is an example:
				// e.g. we now have this slots mapping
				// Length
				//		- measure1
				//		- measure2
				// Color
				//		- Measures Group(2)
				//
				// now we move measure2 into `Color` and put it before `Measures Group(2)`, `setDataItems()` will be called with a
				// `dataItemIdList` including measure2's dataItemId and a multimeasure dataItemId, inside `setDataItems`, measure2 will be removed from original
				// slot(ie, Length) first, removing it will remove the related multimeasure data item(see _processMultiMeasure), then
				// `addDataItemsMappingthen` is called with the `dataItemIdList`, in this case, `dataItemIdList` contains the id of the deleted
				// multimeasure data item, so getDataItemImpl(id) will return null for the multimeasure data item.
				if(!dataItem) {
					dataItemIdList.splice(index, 1);
					return;
				}
				if (dataItem.getSlot()){
					throw new Error('Cannot map an already mapped data item, it must to be unmapped first. The dataitem with id "'+ id
						+ '" is mapped to slot "' + dataItem.getSlot().getId() + '"');
				}
			});
			const actualPosition = position === -1 ? this.getDataItemList().length : position;

			this.dataModel.moveDataItemsToView(dataItemIdList, this.resolveDataViewId());
			this.slotMappingModel.mapDataItemsToSlot(dataItemIdList, this.getId(), position);
			dataItemIdList.forEach((id, index) => {
				// If one of the dataitems is a measure series, then mark this slot as the preferred measure series slot.
				if (id.indexOf(SlotAPIHelper.MULTI_MEASURES_SERIES) > -1) {
					const dataset = this.getDefinition().getDatasetIdList()[0];
					this.slotMappingModel.setMultiMeasureSlotId(dataset, this.getId());
					this.slotMappingModel.setMultiMeasurePosition(dataset, actualPosition + index);
				}
				this.slotsImpl.getDataItemImpl(id).setSlot(this.getAPI(), transactionToken);
			});
			this._processMultiMeasure();
		}

		getDataItemList(ignoreDefaultDataItemMapping = false) {
			return this._getDataItemListWithOptions(ignoreDefaultDataItemMapping);
		}

		_getDataItemListWithOptions(ignoreDefaultDataItemMapping) {
			// TODO - livewidget cleanup - cache and free the list for performance
			const idList = this.slotMappingModel.getMappedDataItemIdList(this.getId());
			let list = idList.map((id) => {
				const dataItem = this.slotsImpl.getDataItem(id);
				if (!dataItem) {
					throw new Error ('The referenced data item \'' + id +'\' is not found in the widget.');
				}
				return dataItem;
			});

			if (list.length === 0 && !ignoreDefaultDataItemMapping) {
				const defaultFromSlot = this.getDefinition().getDefaultFromSlot();
				if (defaultFromSlot && defaultFromSlot.id) {
					const slot = this.slotsImpl.getSlot(defaultFromSlot.id);
					if (slot) {
						list = slot.getDataItemList();
					}
				}
			}
			return list;
		}

		supportsColumns(metadataColumnList) {
			return !this.itemsNotSupported(metadataColumnList);
		}

		/**
		* @param metadata either an array of metadatacolumns or a single dataitemapi which both have access to their taxonomy(the class and family of the data column)
		* @returns a boolean whether the slot supports the taxonomy or datatype of the metadata item(s).
		* This method will check to make sure that the columns being dropped into the slots have the correct taxonomy class and family as per these rules:
		* 1) If the slot has no taxonomy defined, then it accepts all data columns
		* 2) If the slot is a lot/long, then it can accept columns that are also lat/long or if they do not have a taxonomy defined, then the slot will also accept numbers
		* 3) If the slot has a taxonomy defined which is not lat/long then it accepts everything except for lat/long columns
		*/
		//jshint maxdepth:4
		itemsNotSupported(metadata){
			const slot = this.getDefinition();
			if (slot.getSubType()){
				if (_.isArray(metadata)){
					if(this._itemsNotSupportedFromTree(metadata)){
						return true;
					}
				} else{
					if (this._itemsNotSupportedFromSlots(metadata)){
						return true;
					}
				}
			}
			return false;
		}

		_isLatLong() {
			const slot = this.getDefinition();
			return slot.getSubType() === 'latitude' || slot.getSubType() === 'longitude';
		}

		_itemsNotSupportedFromTree(metadata){
			var i;
			for (i=0; i<metadata.length; i++){
				var metadataItem = metadata[i];
				if (this._itemsNotSupportedHelper(metadataItem)){
					return true;
				}
			}
		}

		//this block handles cases where you are swapping slots
		_itemsNotSupportedFromSlots(metadata){
			if(this._itemsNotSupportedHelper(metadata)){
				return true;
			}
		}

		_itemsNotSupportedHelper(metadataItem){
			const slot = this.getDefinition();
			var taxonomy = metadataItem.getTaxonomyList() ? metadataItem.getTaxonomyList()[0] : null;
			if (this._isLatLong()){
				if (taxonomy){
					if (!((taxonomy.getFamily() === 'cLatitude' && slot.getSubType() === 'latitude') || (taxonomy.getFamily() === 'cLongitude' && slot.getSubType() === 'longitude'))){
						return true;
					}
				} else if(!this._isSupportedDataType(metadataItem.getDataType())){
					return true;
				}
			} else{
				if (taxonomy && (taxonomy.getFamily() === 'cLatitude' || taxonomy.getFamily() === 'cLongitude')){
					return true;
				}
			}
		}

		_isSupportedDataType (datatype){
			if (this._isLatLong()){
				return ['integer','decimal','double','float'].indexOf(datatype) > -1;
			}
			return true;
		}


		getDefinition() {
			return this.slotDefinitionAPI;
		}

		isStacked() {
			const mappedDataItems = this.slotMappingModel.getMappedDataItemIdList(this.getId());
			return !!((mappedDataItems.length > 1 &&
				this.getDefinition().isStackItems()));
		}

		hasUnavailableMetadataColumns() {
			const unavailableColumn = _.find(this.getDataItemList(), dataItem => {
				return dataItem.isColumnUnavailable();
			});
			return !!(unavailableColumn);
		}

		_convertTransactionTokenToLegacyOptions(transactionToken){
			const oldOptions = {};
			if(transactionToken){
				oldOptions.payloadData = {
					transactionToken: transactionToken
				};
				if(transactionToken.transactionId){
					oldOptions.payloadData.undoRedoTransactionId = transactionToken.transactionId;
				}
			}

			return oldOptions;
		}
	}

	return SlotImpl;
});
